import DataToolbar from '@/components/DataToolbar';
import DatePeriodNavigation from '@/components/DatePeriodNavigation';
import { getActiveStore } from "@/lib/scope";
import { Link } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import PageHeader from "@/components/PageHeader";
import ReportActions from '@/components/ReportActions';
import WriteButton from "@/components/WriteButton";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, CalendarIcon, Printer, RotateCcw } from "lucide-react";
import { printClosing } from "@/lib/printShift";
import { useSupervisorApproval } from "@/components/SupervisorApprovalDialog";

const fmt = (d) => format(d, "yyyy-MM-dd");
const label = (d) => format(d, "d MMM yyyy", { locale: idLocale });

export default function Shifts() {
  const { approveAction, approvalDialog } = useSupervisorApproval();
  const [shifts, setShifts] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [storeName, setStoreName] = useState("");
  const [range, setRange] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [userId, setUserId] = useState("all");
  const loadSequence = useRef(0);
  const [compact, setCompact] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => { const media = window.matchMedia("(max-width: 767px)"), change = (event) => setCompact(event.matches); media.addEventListener("change", change); return () => media.removeEventListener("change", change); }, []);

  const start = range?.from ? fmt(range.from) : null;
  const end = range?.to ? fmt(range.to) : (range?.from ? fmt(range.from) : null);

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    try {
      const params = {};
      if (start && end) { params.start = start; params.end = end; }
      if (userId !== "all") params.user_id = userId;
      const { data } = await api.get("/shifts", { params });
      if (sequence === loadSequence.current) setShifts(data);
    } catch (e) { if (sequence === loadSequence.current) toast.error(apiError(e)); }
  }, [start, end, userId]);
  useEffect(() => { const sequence = loadSequence; load(); return () => { sequence.current++; }; }, [load]);

  useEffect(() => {
    api.get("/users").then((r) => setCashiers(r.data)).catch(() => {});
    api.get("/stores").then((r) => {
      const sid = getActiveStore();
      const st = r.data.find((s) => s.id === sid) || r.data.find((s) => s.is_default) || r.data[0];
      if (st) setStoreName(st.name);
    }).catch(() => {});
  }, []);

  const dt = (v) => (v ? new Date(v).toLocaleString("id-ID") : "-");

  const preset = (days, mode) => {
    const now = new Date();
    if (mode === "today") setRange({ from: now, to: now });
    else if (mode === "month") setRange({ from: new Date(now.getFullYear(), now.getMonth(), 1), to: now });
    else { const f = new Date(now); f.setDate(f.getDate() - (days - 1)); setRange({ from: f, to: now }); }
    setCalOpen(false);
  };

  const rangeText = range?.from
    ? (range.to && fmt(range.to) !== fmt(range.from) ? `${label(range.from)} — ${label(range.to)}` : label(range.from))
    : "Semua tanggal";

  const totals = useMemo(() => {
    const closed = shifts.filter((s) => s.status === "closed");
    return {
      count: shifts.length,
      revenue: closed.reduce((a, s) => a + (s.summary?.revenue || 0), 0),
      selisih: closed.reduce((a, s) => a + (s.selisih || 0), 0),
    };
  }, [shifts]);

  const reopen = async (shift) => {
    let approval;
    try { approval = await approveAction("reopen_shift", `Buka kembali shift ${shift.user_name} · ${dt(shift.opened_at)}`); }
    catch (error) { return toast.error(apiError(error)); }
    if (approval === false) return;
    try { await api.post(`/shifts/${shift.id}/reopen`, {}, approval); toast.success("Shift dibuka kembali"); load(); }
    catch (error) { toast.error(apiError(error)); }
  };

  return (
    <>
      {approvalDialog}
      
      <PageHeader title="Riwayat Shift" subtitle="Buka/tutup shift kasir & selisih kas">
        <Button variant="outline" asChild><Link to="/devices">Perangkat & Sif Aktif</Link></Button>
        
      </PageHeader>
      <div className="flex-1 min-w-0 overflow-y-auto p-3 sm:p-5 space-y-3 sm:space-y-4" data-testid="shifts-page">
        <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
          <Stat label="Jumlah Shift" value={totals.count} />
          <Stat label="Total Penjualan (tutup)" value={rupiah(totals.revenue)} />
          <Stat label="Total Selisih" value={rupiah(totals.selisih)} cls={totals.selisih === 0 ? "" : totals.selisih > 0 ? "text-accent-readable" : "text-danger-readable"} />
        </div>
        <DataToolbar label="Alat tabel riwayat sif" actions={<ReportActions title="Riwayat Sif" headers={['ID Sif','Kasir','Buka','Tutup','Status','Kas Seharusnya','Kas Dihitung','Selisih']} rows={shifts.map(s => [s.id,s.user_name,dt(s.opened_at),dt(s.closed_at),s.status,s.status === 'closed' ? s.cash_expected : null,s.status === 'closed' ? s.cash_counted : null,s.status === 'closed' ? s.selisih : null])} notes={[`Cabang: ${storeName}`,`Periode: ${rangeText}`,`Filter kasir: ${userId}`, 'Untuk rekap periode lengkap tersedia Laporan → Closing. Sif terbuka tidak dianggap sudah dihitung.']} />}>
<div className="flex min-w-0 flex-wrap items-center gap-2">
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger data-testid="shift-cashier-filter" className="h-9 w-full border-ui-line bg-ui-card sm:h-10 sm:w-[170px]">
              <SelectValue placeholder="Semua kasir" />
            </SelectTrigger>
            <SelectContent className="bg-ui-card border-ui-line text-ui-text">
              <SelectItem value="all">Semua Kasir</SelectItem>
              {cashiers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <DatePeriodNavigation start={start} end={end} label="Periode sif" testId="shift-date" onChange={next => { setRange({from:new Date(`${next.start}T00:00:00`),to:new Date(`${next.end}T00:00:00`)}); setCalOpen(false); }}>
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <Button data-testid="shift-date-btn" variant="outline" className="h-9 max-w-full min-w-0 gap-1.5 border-ui-line bg-ui-card px-2 hover:bg-ui-card sm:h-10 sm:gap-2 sm:px-3">
                <CalendarIcon className="h-4 w-4 text-brand-readable" />
                <span className="max-w-[180px] truncate text-xs sm:text-sm">{rangeText}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto max-w-[calc(100vw-1rem)] p-0 bg-ui-card border-ui-line text-ui-text" data-testid="shift-calendar">
              <div className="flex flex-wrap gap-1 p-2 sm:gap-1.5 sm:p-3 border-b border-ui-line">
                <Chip onClick={() => { setRange(null); setCalOpen(false); }}>Semua</Chip>
                <Chip onClick={() => preset(0, "today")}>Hari ini</Chip>
                <Chip onClick={() => preset(7)}>7 Hari</Chip>
                <Chip onClick={() => preset(30)}>30 Hari</Chip>
                <Chip onClick={() => preset(0, "month")}>Bulan Ini</Chip>
              </div>
              <Calendar mode="range" selected={range} onSelect={(r) => setRange(r)} numberOfMonths={compact ? 1 : 2} locale={idLocale} />
            </PopoverContent>
          </Popover>
          </DatePeriodNavigation>
        </div>
</DataToolbar>
<div className="hidden md:block rounded-xl border border-ui-line bg-ui-soft overflow-x-auto">
          <table className="w-full text-sm" data-testid="shifts-table">
            <thead className="text-ui-muted text-[11px] uppercase tracking-wide border-b border-ui-line">
              <tr>
                <th className="text-left py-2 px-3">Kasir</th>
                <th className="text-left py-2 px-3">Buka</th>
                <th className="text-left py-2 px-3">Tutup</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3">Transaksi</th>
                <th className="text-right py-2 px-3">Penjualan</th>
                <th className="text-right py-2 px-3">Kas Seharusnya</th>
                <th className="text-right py-2 px-3">Kas Dihitung</th>
                <th className="text-right py-2 px-3">Selisih</th>
                <th className="text-center py-2 px-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {shifts.length === 0 ? (
                <tr><td colSpan={10} className="py-10 text-center text-ui-muted"><Clock className="h-6 w-6 mx-auto mb-2 opacity-50" />Belum ada shift pada filter ini</td></tr>
              ) : shifts.map((s) => {
                const sm = s.summary || {};
                const sel = s.selisih ?? 0;
                return (
                  <tr key={s.id} data-testid={`shift-row-${s.id}`} className="border-b border-ui-line hover:bg-ui-soft">
                    <td className="py-2 px-3 font-medium">{s.user_name}</td>
                    <td className="py-2 px-3 text-ui-muted">{dt(s.opened_at)}</td>
                    <td className="py-2 px-3 text-ui-muted">{dt(s.closed_at)}</td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.status === "open" ? "bg-brand-soft text-brand-readable" : "bg-ui-muted-surface/70 text-ui-muted"}`}>
                        {s.status === "open" ? "Buka" : "Tutup"}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right font-mono">{sm.tx_count ?? "-"}</td>
                    <td className="py-2 px-3 text-right font-mono">{s.status === "closed" ? rupiah(sm.revenue || 0) : "-"}</td>
                    <td className="py-2 px-3 text-right font-mono">{s.status === "closed" ? rupiah(s.cash_expected || 0) : "-"}</td>
                    <td className="py-2 px-3 text-right font-mono">{s.status === "closed" ? rupiah(s.cash_counted || 0) : "-"}</td>
                    <td className={`py-2 px-3 text-right font-mono font-bold ${sel === 0 ? "text-ui-muted" : sel > 0 ? "text-accent-readable" : "text-danger-readable"}`}>
                      {s.status === "closed" ? rupiah(sel) : "-"}
                    </td>
                    <td className="py-2 px-3 text-center">
                      {s.status === "closed" ? (
                        <div className="flex items-center justify-center gap-1"><button
                          data-testid={`shift-print-${s.id}`}
                          onClick={() => printClosing(s, s.summary, storeName)}
                          title="Cetak laporan tutup shift"
                          className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-ui-line bg-ui-card text-brand-readable hover:bg-brand-soft hover:border-brand/50 no-select"
                        >
                          <Printer className="h-4 w-4" />
                        </button><WriteButton plain data-testid={`shift-reopen-${s.id}`} onClick={() => reopen(s)} title="Buka kembali shift" className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-ui-line bg-ui-card text-warning-readable hover:bg-warning-soft"><RotateCcw className="h-4 w-4" /></WriteButton></div>
                      ) : <span className="text-ui-muted text-xs">-</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="space-y-2 md:hidden" data-testid="shifts-mobile-list">
          {shifts.length === 0 ? <div className="rounded-xl border border-ui-line bg-ui-soft p-6 text-center text-sm text-ui-muted"><Clock className="mx-auto mb-2 h-6 w-6 opacity-50" />Belum ada shift pada filter ini</div> : shifts.map((s) => {
            const sm = s.summary || {};
            const sel = s.selisih ?? 0;
            return <article key={s.id} data-testid={`shift-mobile-row-${s.id}`} className="min-w-0 rounded-xl border border-ui-line bg-ui-soft p-3">
              <div className="flex min-w-0 items-start justify-between gap-2">
                <div className="min-w-0"><h2 className="break-words text-sm font-semibold">{s.user_name}</h2><p className="mt-1 break-words text-[11px] text-ui-muted">Buka: {dt(s.opened_at)}</p>{s.closed_at && <p className="break-words text-[11px] text-ui-muted">Tutup: {dt(s.closed_at)}</p>}</div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.status === "open" ? "bg-brand-soft text-brand-readable" : "bg-ui-muted-surface/70 text-ui-muted"}`}>{s.status === "open" ? "Buka" : "Tutup"}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-ui-line pt-2 text-xs">
                <MobileValue label="Transaksi" value={sm.tx_count ?? "-"} />
                <MobileValue label="Penjualan" value={s.status === "closed" ? rupiah(sm.revenue || 0) : "-"} />
                <MobileValue label="Kas seharusnya" value={s.status === "closed" ? rupiah(s.cash_expected || 0) : "-"} />
                <MobileValue label="Kas dihitung" value={s.status === "closed" ? rupiah(s.cash_counted || 0) : "-"} />
                <MobileValue label="Selisih" value={s.status === "closed" ? rupiah(sel) : "-"} valueClass={sel === 0 ? "text-ui-muted" : sel > 0 ? "text-accent-readable" : "text-danger-readable"} />
              </div>
              {s.status === "closed" && <div className="mt-3 flex justify-end gap-2 border-t border-ui-line pt-2">
                <button data-testid={`shift-print-${s.id}`} onClick={() => printClosing(s, s.summary, storeName)} title="Cetak laporan tutup shift" aria-label="Cetak laporan tutup shift" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-ui-line bg-ui-card text-brand-readable"><Printer className="h-4 w-4" /></button>
                <WriteButton plain data-testid={`shift-reopen-${s.id}`} onClick={() => reopen(s)} title="Buka kembali shift" aria-label="Buka kembali shift" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-ui-line bg-ui-card text-warning-readable"><RotateCcw className="h-4 w-4" /></WriteButton>
              </div>}
            </article>;
          })}
        </div>
      </div>
    </>
  );
}

function Chip({ children, onClick }) {
  return (
    <button onClick={onClick} className="px-2.5 h-8 rounded-lg text-xs font-medium bg-ui-card border border-ui-line hover:border-brand text-ui-text no-select">
      {children}
    </button>
  );
}

function Stat({ label, value, cls = "" }) {
  return (
    <div className="min-w-0 rounded-xl border border-ui-line bg-ui-soft p-3 sm:p-4">
      <div className="text-[10px] sm:text-[11px] text-ui-muted uppercase tracking-wide">{label}</div>
      <div className={`break-all font-mono font-extrabold text-base sm:text-xl mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

function MobileValue({ label, value, valueClass = "" }) {
  return <div className="min-w-0"><div className="text-[10px] text-ui-muted">{label}</div><div className={`break-all font-mono text-xs font-semibold ${valueClass}`}>{value}</div></div>;
}
